import React, { useEffect, useRef, useState } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { useNavigate } from "react-router-dom";
import { MissionSection } from "../components/Mission";
import axios from "axios";
import Notiflix from "notiflix";
import ReactMarkdown from "react-markdown";

const API_BASE = process.env.REACT_APP_MEMBERSHIP_API || "http://localhost:5001";

/*
 * LAMNA by ZelTechnology — the member AI chat page.
 *
 * Privacy by design: messages live ONLY in React state while this page is
 * open. Nothing is stored in localStorage, sessionStorage, or the database.
 * Closing or navigating away destroys the conversation entirely.
 *
 * LAMNA is powered by the ZelTechnology backend (/api/chat), which proxies
 * to an AI model via OpenRouter server-side. API keys never touch the
 * browser. Messages transit the AI provider for processing but are never
 * stored by ZelTechnology.
 */

const Lamna = () => {
  useDocTitle("ZelT | LAMNA");
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [botTyping, setBotTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("zelt_token");
    if (!token) {
      navigate("/membership");
    }
  }, [navigate]);

  useEffect(() => {
    // scroll the messages container (not the page) to the newest message
    const box = messagesEndRef.current?.parentElement;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages, botTyping]);

  const pushBotMessage = (text) => {
    setMessages((m) => [...m, { from: "lamna", text }]);
  };

  useEffect(() => {
    pushBotMessage(
      "🦈 Welcome to LAMNA by ZelTechnology! I'm your AI assistant — ask me anything about your computer, staying safe online, or tech in general. I speak plain language, promise."
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || botTyping) return;
    const token = localStorage.getItem("zelt_token");
    setMessages((m) => [...m, { from: "member", text }]);
    setInput("");
    setBotTyping(true);

    // Build the conversation (excluding our local welcome message) and
    // send it to the backend proxy. The backend injects LAMNA's persona
    // server-side; the browser never sees any AI credentials.
    const conversation = messages
      .filter((m) => m.from === "member" || !m.text.startsWith("🦈 Welcome"))
      .map((m) => ({
        role: m.from === "member" ? "user" : "assistant",
        content: m.text,
      }));

    axios
      .post(
        `${API_BASE}/api/chat`,
        { messages: [...conversation, { role: "user", content: text }] },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 45000 }
      )
      .then((response) => {
        setMessages((m) => [...m, { from: "lamna", text: response.data.reply }]);
      })
      .catch((error) => {
        const msg =
          error.response?.data?.message ||
          "🦈 I hit a rough current getting your answer. Please try again in a moment.";
        setMessages((m) => [...m, { from: "lamna", text: msg }]);
      })
      .finally(() => setBotTyping(false));
  };

  const clearChat = () => {
    setMessages([]);
    pushBotMessage("🧹 Conversation wiped from memory. Fresh start!");
  };

  const [escalating, setEscalating] = useState(false);

  // ZelGuardian Monthly Checkup — 5 honest self-report questions (0-5 "yes")
  const CHECKUP_ITEMS = [
    "My operating system and browser are up to date",
    "I use different passwords on important accounts (or a password manager)",
    "Two-factor authentication is on for my email and bank",
    "I have a recent backup of my important files",
    "I know how to spot scam messages (urgency, links, codes)",
  ];
  const [checkupOpen, setCheckupOpen] = useState(false);
  const [checkupAnswers, setCheckupAnswers] = useState({});
  const [checkupSaving, setCheckupSaving] = useState(false);
  const [checkupDone, setCheckupDone] = useState(false);

  const saveCheckup = () => {
    const count = CHECKUP_ITEMS.filter((_, i) => checkupAnswers[i]).length;
    setCheckupSaving(true);
    axios
      .post(
        `${API_BASE}/my-health/checkup`,
        { count },
        { headers: { Authorization: `Bearer ${localStorage.getItem("zelt_token")}` } }
      )
      .then(() => {
        setCheckupSaving(false);
        setCheckupDone(true);
        Notiflix.Notify.success(
          `🛡️ Checkup saved — ${count}/5 protections confirmed. ZelGuardian is watching out for you.`
        );
      })
      .catch(() => {
        setCheckupSaving(false);
        Notiflix.Notify.failure("Couldn't save your checkup — try again.");
      });
  };

  const escalate = () => {
    if (escalating) return;
    const convo = messages.filter((m) => m.from === "member");
    if (convo.length === 0) {
      Notiflix.Notify.warning("Ask LAMNA something first — then escalate if she couldn't help.");
      return;
    }
    Notiflix.Confirm.show(
      "Escalate to a human?",
      "A ZelTechnology human will review a short summary of this conversation and reply as a ticket. The conversation itself stays ephemeral — only your approved summary is kept. Continue?",
      "Yes, escalate",
      "Cancel",
      () => {
        setEscalating(true);
        const lastQuestion = convo[convo.length - 1]?.text || "General tech help needed";
        axios
          .post(
            `${API_BASE}/api/chat/escalate`,
            {
              subject: `LAMNA follow-up: ${lastQuestion.slice(0, 80)}`,
              summary: `Member discussed with LAMNA:\n${convo
                .map((m) => `- ${m.text.slice(0, 200)}`)
                .join("\n")}\n\n(LAMNA could not fully resolve this — human follow-up requested.)`,
            },
            { headers: { Authorization: `Bearer ${localStorage.getItem("zelt_token")}` } }
          )
          .then((response) => {
            Notiflix.Report.success(
              `Ticket #${response.data.ticket.id} created 🎫`,
              "A human from ZelTechnology will reply. Track it in Member Area → My Requests.",
              "Okay"
            );
            setMessages((m) => [
              ...m,
              {
                from: "lamna",
                text: "✅ Escalated! A real human from ZelTechnology will follow up on this as ticket #" + response.data.ticket.id + ". Track it in the Member Area → My Requests.",
              },
            ]);
          })
          .catch((err) => {
            Notiflix.Report.failure(
              "Escalation failed",
              err.response?.data?.message || "Please try again or use the contact page.",
              "Okay"
            );
          })
          .finally(() => setEscalating(false));
      },
      () => {}
    );
  };

  return (
    <div>
      <NavBar />
      <div
        id="lamna"
        className="w-full py-12 lg:py-16"
        style={{
          background: "linear-gradient(160deg, #0f172a 0%, #36395a 55%, #1e3a8a 100%)",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <div className="mx-4 lg:mx-20">
          {/* header */}
          <div className="text-center mb-8" data-aos="zoom-in">
            <div className="flex items-center justify-center gap-3">
              <span className="text-4xl">🦈</span>
              <h1 className="text-4xl md:text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-teal-300 via-blue-300 to-purple-300">
                LAMNA
              </h1>
            </div>
            <p className="text-teal-200 font-semibold tracking-wide mt-1">
              Your ZelGuardian · by ZelTechnology
            </p>
            <p className="text-gray-300 max-w-2xl mx-auto mt-3 text-sm md:text-base">
              Our AI assistant — always swimming, never stopping. Ask about
              your computer, staying safe online, or anything tech.
            </p>
            <span className="inline-block mt-3 px-4 py-1 rounded-full text-xs font-bold bg-teal-400/20 text-teal-200 border border-teal-400/40">
              🦈 LIVE · conversations are never saved
            </span>
          </div>

          {/* why LAMNA exists — mission strip */}
          <div
            className="max-w-3xl mx-auto mb-8 rounded-2xl p-5 text-center border border-teal-400/30"
            style={{ background: "rgba(255,255,255,0.06)" }}
            data-aos="fade-up"
          >
            <p className="text-teal-200 text-sm md:text-base leading-relaxed">
              🦈 <span className="font-bold text-white">Why LAMNA swims:</span>{" "}
              like the lamna shark that never stops moving, ZelTechnology never
              stops serving — free IT help for anyone navigating an
              overwhelming digital age, honest protection from digital
              predators, and opportunity for those who have served our
              country. LAMNA is that mission, learning to talk.
            </p>
          </div>

          {/* ZelGuardian Monthly Checkup */}
          <div className="max-w-3xl mx-auto mb-8" data-aos="fade-up">
            <div className="bg-white/5 rounded-2xl border border-teal-400/30 overflow-hidden">
              <button
                onClick={() => setCheckupOpen(!checkupOpen)}
                className="w-full p-5 flex justify-between items-center text-left"
              >
                <div>
                  <p className="font-extrabold text-white">🛡️ ZelGuardian Monthly Checkup</p>
                  <p className="text-teal-200 text-xs mt-0.5">
                    5 quick yes/no items — takes 60 seconds, boosts your Protection Score.
                  </p>
                </div>
                <span className={`text-teal-300 text-xl transition-transform ${checkupOpen ? "rotate-180" : ""}`}>
                  ▾
                </span>
              </button>
              {checkupOpen && (
                <div className="px-5 pb-5 border-t border-white/10 pt-4">
                  {checkupDone ? (
                    <p className="text-teal-300 text-sm font-semibold">
                      ✅ Checkup complete — your Protection Score in the Member
                      Area has been updated. See you next month! 🦈
                    </p>
                  ) : (
                    <>
                      <p className="text-gray-300 text-sm mb-4">
                        For each item, confirm it's currently true on your
                        main computer:
                      </p>
                      <div className="space-y-2">
                        {CHECKUP_ITEMS.map((item, i) => (
                          <label
                            key={i}
                            className="flex items-center gap-3 bg-white/5 rounded-xl p-3 cursor-pointer hover:bg-white/10 transition"
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(checkupAnswers[i])}
                              onChange={(e) =>
                                setCheckupAnswers({
                                  ...checkupAnswers,
                                  [i]: e.target.checked,
                                })
                              }
                              className="w-4 h-4 accent-teal-400"
                            />
                            <span className="text-gray-200 text-sm">{item}</span>
                          </label>
                        ))}
                      </div>
                      <button
                        onClick={saveCheckup}
                        disabled={checkupSaving}
                        className="mt-4 w-full py-2.5 rounded-xl font-bold text-blue-900 disabled:opacity-50"
                        style={{ background: "linear-gradient(90deg, #12bfbe, #9198e5)" }}
                      >
                        {checkupSaving ? "Saving…" : "🛡️ Save My Checkup"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* chat window */}
          <div
            className="max-w-3xl mx-auto rounded-3xl shadow-2xl overflow-hidden border border-teal-400/30"
            data-aos="fade-up"
          >
            {/* chat header bar */}
            <div
              className="flex items-center justify-between px-6 py-4"
              style={{ background: "linear-gradient(90deg, #12bfbe, #9198e5)" }}
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl">
                  🦈
                </span>
                <div>
                  <p className="font-bold text-white leading-tight">LAMNA</p>
                  <p className="text-white/80 text-xs">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-300 mr-1 animate-pulse"></span>
                    online · powered by AI
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={escalate}
                  disabled={escalating}
                  className="text-blue-900 hover:text-blue-700 text-sm font-semibold bg-white/30 hover:bg-white/50 px-3 py-1.5 rounded-lg transition disabled:opacity-50"
                  aria-label="Escalate to a human"
                  title="Have a human review this conversation"
                >
                  👤 Escalate
                </button>
                <button
                  onClick={clearChat}
                  className="text-white/90 hover:text-white text-sm font-semibold bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition"
                  aria-label="Clear conversation"
                >
                  🧹 Clear
                </button>
              </div>
            </div>

            {/* messages */}
            <div
              className="px-4 md:px-6 py-6 space-y-4 overflow-y-auto"
              style={{ height: "55vh", background: "#0f172a" }}
              role="log"
              aria-live="polite"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.from === "member" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm md:text-base leading-relaxed shadow-lg ${
                      m.from === "member"
                        ? "bg-blue-900 text-white rounded-br-md"
                        : "bg-gradient-to-br from-teal-500/90 to-blue-500/90 text-white rounded-bl-md"
                    }`}
                  >
                    {m.from === "lamna" ? (
                      <div className="lamna-reply prose-invert">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ol: ({ children }) => <ol className="list-decimal ml-5 space-y-1 my-2">{children}</ol>,
                            ul: ({ children }) => <ul className="list-disc ml-5 space-y-1 my-2">{children}</ul>,
                            li: ({ children }) => <li className="pl-1">{children}</li>,
                            strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                            em: ({ children }) => <em className="italic">{children}</em>,
                            code: ({ children }) => (
                              <code className="bg-blue-950/80 text-teal-200 px-1.5 py-0.5 rounded text-xs font-mono">
                                {children}
                              </code>
                            ),
                            h1: ({ children }) => <p className="font-bold text-white text-base my-2">{children}</p>,
                            h2: ({ children }) => <p className="font-bold text-white text-base my-2">{children}</p>,
                            h3: ({ children }) => <p className="font-bold text-white text-sm my-2">{children}</p>,
                            a: ({ children, href }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {m.text}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      m.text
                    )}
                  </div>
                </div>
              ))}
              {botTyping && (
                <div className="flex justify-start">
                  <div className="bg-gradient-to-br from-teal-500/60 to-blue-500/60 text-white px-4 py-3 rounded-2xl rounded-bl-md">
                    <span className="inline-flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-white animate-bounce"></span>
                      <span className="w-2 h-2 rounded-full bg-white animate-bounce" style={{ animationDelay: "0.15s" }}></span>
                      <span className="w-2 h-2 rounded-full bg-white animate-bounce" style={{ animationDelay: "0.3s" }}></span>
                    </span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* input */}
            <form
              onSubmit={send}
              className="flex gap-2 px-4 md:px-6 py-4 bg-black/60 border-t border-teal-400/20"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Swim something over to LAMNA…"
                className="flex-1 px-4 py-3 rounded-xl bg-slate-800 text-white placeholder-gray-400 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                maxLength={500}
                aria-label="Message to LAMNA"
              />
              <button
                type="submit"
                disabled={botTyping || !input.trim()}
                className="px-5 py-3 rounded-xl font-bold text-white shadow-lg disabled:opacity-40 transition"
                style={{ background: "linear-gradient(90deg, #12bfbe, #9198e5)" }}
              >
                Send ➤
              </button>
            </form>
          </div>

          <p className="text-center text-gray-400 text-xs mt-6 max-w-xl mx-auto">
            🔒 This conversation is ephemeral — it exists only in your open
            browser and is deleted the moment you leave. No servers, no logs,
            no storage.
          </p>

          {/* full mission pillars, compact variant */}
          <div className="mt-14">
            <MissionSection compact />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Lamna;
